import { useCallback } from 'react';
import { Card, Tooltip } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTag } from 'src/components/CustomTag';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolCardImage } from 'src/components/GenerationToolCardImage';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { GENERATION_TOOL, GENERATION_TOOL_CARD_NEW_DOCUMENT } from 'src/data/generationTool';
import { useTemplateTags } from 'src/lib/hooks/useTemplateTags';

import { formatTooltipLine, generateToolListTooltip } from '../../lib';

import { useStyles } from './useStyles';

export const CardLarge = ({
  className,
  classes,
  value,
  title,
  description,
  copy,
  selected,
  onSelect,
  disabled,
  toolList,
  customMultiPromptId,
  customMultiPromptGroup,
  customMultiPromptProjectType,
  largeImage,
  showMultiPromptTag,
  isCsvTemplate,
  hideToolIcons,
  showComingSoonTag,
}) => {
  const styles = useStyles({ classes });

  const { tagLabel, tagColor } = useTemplateTags({
    isCsvTemplate,
  });

  const handleClick = useCallback(() => {
    const newValue = {
      ...value,
      customMultiPromptId: customMultiPromptId || undefined,
      customMultiPromptGroup: customMultiPromptGroup || undefined,
      customMultiPromptProjectType,
    };
    onSelect(newValue);
  }, [onSelect, value, customMultiPromptId, customMultiPromptGroup, customMultiPromptProjectType]);

  const tooltipTitle =
    toolList.length > 1 ? generateToolListTooltip(toolList) : formatTooltipLine(title, description);

  return (
    <Card
      className={cx(styles.root, className, {
        [styles.selected]: selected,
        [styles.disabled]: disabled,
      })}
      onClick={disabled ? null : handleClick}
      data-testid={`templateButton_${value.tool}`}
    >
      <GenerationToolCardImage
        className={styles.icon}
        tool={value.tool}
        svgStringImage={largeImage}
      />

      <div>
        <div className={styles.copyWrapper}>
          <CustomTypography
            className={styles.title}
            variant={TYPOGRAPHY_VARIANT.medium14}
          >
            <span>{title}</span>
            {description && <span>{' - ' + description}</span>}
          </CustomTypography>
          <CustomTypography
            className={styles.description}
            variant={TYPOGRAPHY_VARIANT.paragraph12}
          >
            {copy}
          </CustomTypography>
        </div>
        <div className={styles.footer}>
          {showMultiPromptTag ? (
            <CustomTag
              tag={tagLabel}
              backgroundColor={tagColor}
            />
          ) : (
            <span className={styles.tagHidden} />
          )}

          {showComingSoonTag && (
            <CustomTypography
              className={styles.comingSoonTag}
              variant={TYPOGRAPHY_VARIANT.medium14}
            >
              Coming soon!
            </CustomTypography>
          )}

          {!hideToolIcons && (
            <Tooltip
              title={tooltipTitle}
              arrow
              placement={'bottom'}
            >
              <div className={styles.toolIconsWrapper}>
                {toolList.length > 3 && (
                  <span className={styles.moreTools}>+{toolList.length - 3}</span>
                )}

                {toolList.map(
                  (tool, index) =>
                    index <= 2 && (
                      <GenerationToolIcon
                        key={tool}
                        className={styles.toolIcon}
                        tool={tool}
                      />
                    )
                )}
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </Card>
  );
};

CardLarge.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  value: PropTypes.shape({
    tool: PropTypes.oneOf(
      Object.values({
        ...GENERATION_TOOL,
        [GENERATION_TOOL_CARD_NEW_DOCUMENT]: GENERATION_TOOL_CARD_NEW_DOCUMENT,
      })
    ).isRequired,
    customMultiPromptId: PropTypes.string,
    customMultiPromptGroup: PropTypes.string,
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  copy: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  toolList: PropTypes.array,
  customMultiPromptId: PropTypes.string,
  customMultiPromptGroup: PropTypes.string,
  customMultiPromptProjectType: PropTypes.string,
  showMultiPromptTag: PropTypes.bool,
  largeImage: PropTypes.string,
  isCsvTemplate: PropTypes.bool,
  hideToolIcons: PropTypes.bool,
  showComingSoonTag: PropTypes.bool,
};

CardLarge.defaultProps = {
  className: '',
  classes: {},
  description: '',
  selected: false,
  disabled: false,
  toolList: [],
  customMultiPromptId: null,
  customMultiPromptGroup: null,
  customMultiPromptProjectType: null,
  showMultiPromptTag: false,
  largeImage: null,
  isCsvTemplate: false,
  hideToolIcons: false,
  showComingSoonTag: false,
};
